import axios from "axios";
import IsLoading from "../../../isLoading";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";

import {
  ArrowBack,
  ArrowOutward,
  CheckCircle,
  Close,
  Description,
  Email,
  Event,
  ExpandMore,
  FilePresent,
  Flag,
  Home,
  HowToReg,
  InfoOutlined,
  KeyboardArrowDown,
  LocalPhone,
  LocationOn,
  MenuBook,
  Person,
  School,
  Verified,
  WarningAmber,
  WorkspacePremium,
} from "@mui/icons-material";

import "./applogdetail.css";

export default function AppLogDetail({ setActive }) {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [applicant, setApplicant] = useState(null);
  const [error, setError] = useState("");
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [activeExam, setActiveExam] = useState("first");

  const serverHost = process.env.REACT_APP_SERVER_SCRIPT_HOST;

  const getApplicant = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axios.post(
        `${serverHost}/applicant_log/details/${encodeURIComponent(id)}`
      );

      const applicantData = response?.data?.response;

      if (!applicantData) {
        throw new Error("Applicant details were not found.");
      }

      setApplicant(applicantData);

      enqueueSnackbar(
        `${applicantData?.first_Name || ""} ${
          applicantData?.sur_Name || ""
        } details loaded successfully`,
        {
          variant: "success",
          autoHideDuration: 5000,
        }
      );
    } catch (err) {
      console.error("Applicant details error:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load applicant details."
      );
    } finally {
      setLoading(false);
    }
  }, [id, serverHost, enqueueSnackbar]);

  useEffect(() => {
    getApplicant();
  }, [getApplicant]);

  const fullName = useMemo(() => {
    if (!applicant) return "Applicant";

    return [
      applicant.first_Name,
      applicant.sur_Name,
      applicant.other_Name,
    ]
      .filter(Boolean)
      .join(" ")
      .trim();
  }, [applicant]);

  const admissionStatus = applicant?.admissions?.status || "pending";

  const statusConfig = useMemo(() => {
    const config = {
      admitted: {
        label: "Admitted",
        className: "status-admitted",
        icon: <Verified />,
      },
      recommended: {
        label: "Recommended",
        className: "status-recommended",
        icon: <CheckCircle />,
      },
      "not recommended": {
        label: "Rejected",
        className: "status-rejected",
        icon: <Close />,
      },
      pending: {
        label: "Awaiting review",
        className: "status-pending",
        icon: <WarningAmber />,
      },
    };

    return config[admissionStatus] || config.pending;
  }, [admissionStatus]);

  const formatDate = (value) => {
    if (!value) return "Not provided";

    try {
      return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date(value));
    } catch {
      return value;
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "Not available";

    return value;
  };

  const cleanValue = (value, fallback = "Not provided") => {
    if (value === undefined || value === null || String(value).trim() === "") {
      return fallback;
    }

    return String(value).trim();
  };

  const handleRecommendation = async (type) => {
    try {
      setActionLoading(true);

      const endpoint =
        type === "recommend"
          ? `${serverHost}/applicant_log/recommend/${encodeURIComponent(id)}`
          : `${serverHost}/applicant_log/not_recommend/${encodeURIComponent(id)}`;

      await axios.put(endpoint);

      const nextStatus =
        type === "recommend" ? "recommended" : "not recommended";

      setApplicant((previous) => ({
        ...previous,
        admissions: {
          ...previous.admissions,
          status: nextStatus,
        },
      }));

      enqueueSnackbar(
        type === "recommend"
          ? `${fullName} has been recommended successfully`
          : `${fullName} has been rejected`,
        {
          variant: type === "recommend" ? "success" : "error",
          autoHideDuration: 6000,
        }
      );

      setShowRejectConfirm(false);
    } catch (err) {
      console.error("Application action error:", err);

      enqueueSnackbar(
        err?.response?.data?.message ||
          "Unable to update the application status.",
        {
          variant: "error",
          autoHideDuration: 6000,
        }
      );
    } finally {
      setActionLoading(false);
    }
  };

  const renderStatusAction = () => {
    if (admissionStatus === "admitted") {
      return (
        <div className="ald-admitted-notice">
          <div className="ald-admitted-icon">
            <Verified />
          </div>

          <div>
            <strong>Applicant already admitted</strong>
            <span>
              This application has already completed the admission process.
            </span>
          </div>
        </div>
      );
    }

    if (admissionStatus === "recommended") {
      return (
        <div className="ald-action-status ald-action-status-success">
          <CheckCircle />
          <span>Application recommended</span>
        </div>
      );
    }

    if (admissionStatus === "not recommended") {
      return (
        <div className="ald-action-status ald-action-status-danger">
          <Close />
          <span>Application rejected</span>
        </div>
      );
    }

    return (
      <div className="ald-action-buttons">
        <button
          type="button"
          className="ald-btn ald-btn-danger"
          onClick={() => setShowRejectConfirm(true)}
          disabled={actionLoading}
        >
          <Close />
          Reject application
        </button>

        <button
          type="button"
          className="ald-btn ald-btn-primary"
          onClick={() => handleRecommendation("recommend")}
          disabled={actionLoading}
        >
          <CheckCircle />
          {actionLoading ? "Processing..." : "Recommend applicant"}
        </button>
      </div>
    );
  };

  if (loading) {
    return <IsLoading />;
  }

  if (error || !applicant) {
    return (
      <section className="ald-error-page">
        <div className="ald-error-card">
          <div className="ald-error-icon">
            <WarningAmber />
          </div>

          <h2>Unable to load application</h2>

          <p>{error || "Applicant information could not be found."}</p>

          <button
            type="button"
            className="ald-btn ald-btn-primary"
            onClick={getApplicant}
          >
            Try again
          </button>
        </div>
      </section>
    );
  }

  const primarySchool =
    applicant?.eductional_records?.primary_school || {};

  const juniorSchool =
    applicant?.eductional_records?.junior_secondary_school || {};

  const seniorSchool =
    applicant?.eductional_records?.senior_secondary_school || {};

  const jambDetails = applicant?.eductional_records?.jamb_details || {};

  const firstSitting = applicant?.results?.first_sitting || {};
  const secondSitting = applicant?.results?.second_sitting || {};

  const activeSitting =
    activeExam === "first" ? firstSitting : secondSitting;

  return (
    <>
      <section className="ald-page">
        <div className="ald-shell">

          {/* =========================================================
              TOP NAVIGATION
          ========================================================= */}

          <div className="ald-topbar">
            <button
              type="button"
              className="ald-back-button"
              onClick={() => navigate(-1)}
            >
              <ArrowBack />
              <span>Applications</span>
            </button>

            <div className="ald-topbar-right">
              <span className="ald-record-id">
                Record ID: {applicant.application_number}
              </span>

              <span className={`ald-status ${statusConfig.className}`}>
                {statusConfig.icon}
                {statusConfig.label}
              </span>
            </div>
          </div>

          {/* =========================================================
              APPLICANT HERO
          ========================================================= */}

          <header className="ald-hero">
            <div className="ald-hero-main">

              <div className="ald-avatar-wrapper">
                {applicant?.passport?.url ? (
                  <img
                    src={applicant.passport.url}
                    alt={fullName}
                    className="ald-avatar"
                  />
                ) : (
                  <div className="ald-avatar ald-avatar-placeholder">
                    <Person />
                  </div>
                )}

                <span
                  className={`ald-avatar-status ${
                    admissionStatus === "admitted"
                      ? "is-green"
                      : admissionStatus === "not recommended"
                      ? "is-red"
                      : "is-blue"
                  }`}
                />
              </div>

              <div className="ald-hero-identity">
                <div className="ald-eyebrow">
                  <span>APPLICATION PROFILE</span>
                  <span className="ald-dot" />
                  <span>
                    {cleanValue(applicant.application_number)}
                  </span>
                </div>

                <h1>{fullName}</h1>

                <div className="ald-hero-meta">
                  <span>
                    <School />
                    {cleanValue(applicant.course_of_study)}
                  </span>

                  <span>
                    <MenuBook />
                    Level {cleanValue(applicant.level)}
                  </span>

                  <span>
                    <LocationOn />
                    {cleanValue(applicant.State_of_origin)}
                  </span>
                </div>
              </div>
            </div>

            <div className="ald-hero-action">
              {renderStatusAction()}
            </div>
          </header>

          {/* =========================================================
              QUICK METRICS
          ========================================================= */}

          <div className="ald-metrics">

            <div className="ald-metric-card">
              <div className="ald-metric-icon ald-icon-blue">
                <HowToReg />
              </div>

              <div>
                <span>Registration</span>
                <strong>
                  {cleanValue(
                    applicant?.account_status?.registration_status
                  )}
                </strong>
              </div>
            </div>

            <div className="ald-metric-card">
              <div className="ald-metric-icon ald-icon-green">
                <CheckCircle />
              </div>

              <div>
                <span>Payment</span>
                <strong>
                  {cleanValue(
                    applicant?.account_status?.paymentStatus
                  )}
                </strong>
              </div>
            </div>

            <div className="ald-metric-card">
              <div className="ald-metric-icon ald-icon-purple">
                <WorkspacePremium />
              </div>

              <div>
                <span>Programme</span>
                <strong>
                  {cleanValue(applicant.programme_of_study)}
                </strong>
              </div>
            </div>

            <div className="ald-metric-card">
              <div className="ald-metric-icon ald-icon-orange">
                <Event />
              </div>

              <div>
                <span>Submitted</span>
                <strong>
                  {cleanValue(applicant?.registered_date?.date)}
                </strong>
              </div>
            </div>

          </div>

          {/* =========================================================
              MAIN CONTENT
          ========================================================= */}

          <div className="ald-content">

            {/* =======================================================
                PERSONAL INFORMATION
            ======================================================= */}

            <section className="ald-section">
              <div className="ald-section-heading">
                <div>
                  <span className="ald-section-kicker">01</span>
                  <div>
                    <h2>Personal information</h2>
                    <p>Applicant identity and contact information</p>
                  </div>
                </div>
              </div>

              <div className="ald-card">
                <div className="ald-info-grid">

                  <InfoItem
                    label="Full name"
                    value={fullName}
                    icon={<Person />}
                  />

                  <InfoItem
                    label="Application number"
                    value={applicant.application_number}
                    icon={<Description />}
                  />

                  <InfoItem
                    label="Date of birth"
                    value={formatDate(applicant.Date_of_birth)}
                    icon={<Event />}
                  />

                  <InfoItem
                    label="Gender"
                    value={applicant.gender}
                    icon={<Person />}
                    capitalize
                  />

                  <InfoItem
                    label="Email address"
                    value={applicant.email}
                    icon={<Email />}
                  />

                  <InfoItem
                    label="Phone number"
                    value={`0${cleanValue(applicant.phone_number, "")}`}
                    icon={<LocalPhone />}
                  />

                  <InfoItem
                    label="Marital status"
                    value={applicant.Marital_status}
                    icon={<Person />}
                    capitalize
                  />

                  <InfoItem
                    label="Nationality"
                    value={applicant.Nationality}
                    icon={<LocationOn />}
                  />

                  <InfoItem
                    label="State of origin"
                    value={applicant.State_of_origin}
                    icon={<LocationOn />}
                  />

                  <InfoItem
                    label="LGA of origin"
                    value={applicant.Local_Govt_Area}
                    icon={<LocationOn />}
                  />

                  <InfoItem
                    label="Programme"
                    value={applicant.programme_of_study}
                    icon={<School />}
                  />

                  <InfoItem
                    label="Course of study"
                    value={applicant.course_of_study}
                    icon={<MenuBook />}
                  />

                </div>

                <div className="ald-address-box">
                  <div className="ald-address-icon">
                    <Home />
                  </div>

                  <div>
                    <span>Residential address</span>
                    <p>
                      {cleanValue(applicant.Residential_address)}
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* =======================================================
                ADMISSION
            ======================================================= */}

            <section className="ald-section">
              <div className="ald-section-heading">
                <div>
                  <span className="ald-section-kicker">02</span>
                  <div>
                    <h2>Admission overview</h2>
                    <p>Current admission decision and application state</p>
                  </div>
                </div>
              </div>

              <div className="ald-admission-card">

                <div className="ald-admission-status">
                  <div className={`ald-large-status ${statusConfig.className}`}>
                    {statusConfig.icon}
                  </div>

                  <div>
                    <span>Current admission status</span>
                    <h3>{statusConfig.label}</h3>

                    <p>
                      {admissionStatus === "admitted"
                        ? "The applicant has been formally admitted."
                        : admissionStatus === "recommended"
                        ? "The applicant has been recommended for admission."
                        : admissionStatus === "not recommended"
                        ? "The application has been rejected."
                        : "The application is waiting for an admission decision."}
                    </p>
                  </div>
                </div>

                <div className="ald-admission-details">
                  <div>
                    <span>Acceptance</span>
                    <strong>
                      {cleanValue(
                        applicant?.admissions?.acceptance_of_adm
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>Date admitted</span>
                    <strong>
                      {cleanValue(
                        applicant?.admissions?.date_admitted
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>Payment</span>
                    <strong>
                      {cleanValue(
                        applicant?.account_status?.paymentStatus
                      )}
                    </strong>
                  </div>
                </div>
              </div>
            </section>

            {/* =======================================================
                EDUCATION
            ======================================================= */}

            {applicant?.account_status?.registration_status ===
              "completed" && (
              <section className="ald-section">
                <div className="ald-section-heading">
                  <div>
                    <span className="ald-section-kicker">03</span>
                    <div>
                      <h2>Educational history</h2>
                      <p>Academic institutions and qualifications</p>
                    </div>
                  </div>
                </div>

                <div className="ald-education-layout">

                  <EducationCard
                    number="01"
                    title="Primary education"
                    school={primarySchool.institution_name}
                    start={primarySchool.year_started}
                    finish={primarySchool.year_finish}
                    certificate={primarySchool.certficate_obtained}
                  />

                  <EducationCard
                    number="02"
                    title="Junior secondary"
                    school={juniorSchool.institution_name}
                    start={juniorSchool.year_started}
                    finish={juniorSchool.year_finish}
                    certificate={juniorSchool.certficate_obtained}
                  />

                  <EducationCard
                    number="03"
                    title="Senior secondary"
                    school={seniorSchool.institution_name}
                    start={seniorSchool.year_started}
                    finish={seniorSchool.year_finish}
                    certificate={seniorSchool.certficate_obtained}
                  />

                </div>

                <div className="ald-jamb-card">
                  <div className="ald-jamb-brand">
                    <span>JAMB</span>
                    <small>UTME</small>
                  </div>

                  <div>
                    <span>JAMB registration number</span>
                    <strong>
                      {cleanValue(jambDetails.jamb_number)}
                    </strong>
                  </div>

                  <div>
                    <span>JAMB score</span>
                    <strong className="ald-score">
                      {cleanValue(jambDetails.jamb_score)}
                    </strong>
                  </div>
                </div>
              </section>
            )}

            {/* =======================================================
                EXAMINATION
            ======================================================= */}

            {applicant?.account_status?.registration_status ===
              "completed" && (
              <section className="ald-section">
                <div className="ald-section-heading">
                  <div>
                    <span className="ald-section-kicker">04</span>
                    <div>
                      <h2>Examination results</h2>
                      <p>Secondary school examination performance</p>
                    </div>
                  </div>
                </div>

                <div className="ald-exam-card">

                  <div className="ald-exam-header">
                    <div>
                      <span>Examination record</span>
                      <h3>
                        {cleanValue(activeSitting.exams_type)}
                      </h3>
                    </div>

                    <div className="ald-exam-meta">
                      <div>
                        <span>Year</span>
                        <strong>
                          {cleanValue(activeSitting.exams_year)}
                        </strong>
                      </div>

                      <div>
                        <span>Exam number</span>
                        <strong>
                          {cleanValue(activeSitting.exams_number)}
                        </strong>
                      </div>
                    </div>
                  </div>

                  <div className="ald-exam-tabs">
                    <button
                      type="button"
                      className={
                        activeExam === "first" ? "is-active" : ""
                      }
                      onClick={() => setActiveExam("first")}
                    >
                      First sitting
                    </button>

                    {applicant.No_of_sitting === "2" && (
                      <button
                        type="button"
                        className={
                          activeExam === "second" ? "is-active" : ""
                        }
                        onClick={() => setActiveExam("second")}
                      >
                        Second sitting
                      </button>
                    )}
                  </div>

                  <div className="ald-results-table-wrapper">
                    <table className="ald-results-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Subject</th>
                          <th>Grade</th>
                        </tr>
                      </thead>

                      <tbody>
                        {(activeSitting.grade || [])
                          .filter(
                            (item) =>
                              item?.subject?.trim() ||
                              item?.grade?.trim()
                          )
                          .map((result, index) => (
                            <tr key={`${result.subject}-${index}`}>
                              <td>{String(index + 1).padStart(2, "0")}</td>
                              <td>{cleanValue(result.subject)}</td>
                              <td>
                                <span className="ald-grade">
                                  {cleanValue(result.grade)}
                                </span>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            )}

            {/* =======================================================
                DOCUMENTS
            ======================================================= */}

            <section className="ald-section">
              <div className="ald-section-heading">
                <div>
                  <span className="ald-section-kicker">05</span>
                  <div>
                    <h2>Uploaded documents</h2>
                    <p>Supporting documents submitted with this application</p>
                  </div>
                </div>

                <span className="ald-section-count">
                  {(applicant.document_uploaded || []).length} files
                </span>
              </div>

              <div className="ald-documents-grid">

                {(applicant.document_uploaded || []).map(
                  (document, index) => (
                    <a
                      key={`${document.filename}-${index}`}
                      href={document.url}
                      target="_blank"
                      rel="noreferrer"
                      className="ald-document-card"
                    >
                      <div className="ald-document-preview">
                        {document.mimeType?.startsWith("image/") ? (
                          <img
                            src={document.url}
                            alt={document.filename}
                          />
                        ) : (
                          <FilePresent />
                        )}

                        <span className="ald-document-number">
                          {String(index + 1).padStart(2, "0")}
                        </span>

                        <span className="ald-document-open">
                          <ArrowOutward />
                        </span>
                      </div>

                      <div className="ald-document-info">
                        <div>
                          <span>
                            {document.mimeType || "Document"}
                          </span>

                          <strong>
                            {document.filename}
                          </strong>
                        </div>

                        <ArrowOutward />
                      </div>
                    </a>
                  )
                )}

                {(!applicant.document_uploaded ||
                  applicant.document_uploaded.length === 0) && (
                  <div className="ald-empty-state">
                    <Description />
                    <strong>No documents uploaded</strong>
                    <span>
                      No supporting documents were attached to this
                      application.
                    </span>
                  </div>
                )}

              </div>
            </section>

            {/* =======================================================
                REFEREES
            ======================================================= */}

            {applicant?.account_status?.registration_status ===
              "completed" && (
              <section className="ald-section">
                <div className="ald-section-heading">
                  <div>
                    <span className="ald-section-kicker">06</span>
                    <div>
                      <h2>Referees</h2>
                      <p>Applicant reference contacts</p>
                    </div>
                  </div>
                </div>

                <div className="ald-referee-grid">

                  <RefereeCard
                    number="01"
                    title="First referee"
                    referee={
                      applicant?.refrees?.first_referee
                    }
                  />

                  <RefereeCard
                    number="02"
                    title="Second referee"
                    referee={
                      applicant?.refrees?.second_referee
                    }
                  />

                </div>
              </section>
            )}

            {/* =======================================================
                NEXT OF KIN
            ======================================================= */}

            {applicant?.account_status?.registration_status ===
              "completed" && (
              <section className="ald-section">
                <div className="ald-section-heading">
                  <div>
                    <span className="ald-section-kicker">07</span>
                    <div>
                      <h2>Next of kin</h2>
                      <p>Emergency and family contact information</p>
                    </div>
                  </div>
                </div>

                <div className="ald-nok-card">

                  <div className="ald-nok-avatar">
                    <Person />
                  </div>

                  <div className="ald-nok-main">
                    <span>Next of kin</span>
                    <h3>
                      {cleanValue(
                        applicant?.next_of_kin?.full_name
                      )}
                    </h3>
                  </div>

                  <div>
                    <span>Relationship</span>
                    <strong>
                      {cleanValue(
                        applicant?.next_of_kin?.relationship
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>Phone</span>
                    <strong>
                      {cleanValue(
                        applicant?.next_of_kin?.phone
                      )}
                    </strong>
                  </div>

                  <div className="ald-nok-address">
                    <span>Address</span>
                    <strong>
                      {cleanValue(
                        applicant?.next_of_kin?.address
                      )}
                    </strong>
                  </div>

                </div>
              </section>
            )}

            {/* =======================================================
                AUDIT / SUBMISSION
            ======================================================= */}

            <section className="ald-section">
              <div className="ald-section-heading">
                <div>
                  <span className="ald-section-kicker">08</span>
                  <div>
                    <h2>Application record</h2>
                    <p>Registration and submission metadata</p>
                  </div>
                </div>
              </div>

              <div className="ald-record-card">

                <div>
                  <span>Registered</span>
                  <strong>
                    {formatDateTime(
                      applicant?.registered_date?.date
                    )}
                  </strong>
                  <small>
                    {formatDateTime(
                      applicant?.registered_date?.time
                    )}
                  </small>
                </div>

                <div>
                  <span>Uploaded</span>
                  <strong>
                    {cleanValue(applicant.upload_date)}
                  </strong>
                </div>

                <div>
                  <span>Application status</span>
                  <strong>{statusConfig.label}</strong>
                </div>

                <div>
                  <span>Live record</span>
                  <strong>
                    {applicant.live ? "Active" : "Inactive"}
                  </strong>
                </div>

              </div>
            </section>

          </div>

          {/* =========================================================
              BOTTOM ACTION BAR
          ========================================================= */}

          <div className="ald-bottom-bar">
            <div>
              <span>Application decision</span>
              <strong>{fullName}</strong>
            </div>

            <div className="ald-bottom-actions">
              {renderStatusAction()}
            </div>
          </div>

        </div>
      </section>

      {/* =============================================================
          REJECT CONFIRMATION
      ============================================================= */}

      {showRejectConfirm && (
        <div
          className="ald-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setShowRejectConfirm(false);
            }
          }}
        >
          <div className="ald-confirm-modal">

            <button
              type="button"
              className="ald-modal-close"
              onClick={() => setShowRejectConfirm(false)}
            >
              <Close />
            </button>

            <div className="ald-confirm-icon">
              <WarningAmber />
            </div>

            <span className="ald-confirm-kicker">
              Admission decision
            </span>

            <h2>Reject this application?</h2>

            <p>
              You are about to reject the application submitted by{" "}
              <strong>{fullName}</strong>. This action will update
              the applicant's admission status.
            </p>

            <div className="ald-confirm-actions">
              <button
                type="button"
                className="ald-btn ald-btn-secondary"
                onClick={() => setShowRejectConfirm(false)}
                disabled={actionLoading}
              >
                Cancel
              </button>

              <button
                type="button"
                className="ald-btn ald-btn-danger"
                onClick={() => handleRecommendation("reject")}
                disabled={actionLoading}
              >
                <Close />
                {actionLoading ? "Rejecting..." : "Yes, reject application"}
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
}

/* ================================================================
   REUSABLE INFO ITEM
================================================================ */

function InfoItem({ label, value, icon, capitalize = false }) {
  return (
    <div className="ald-info-item">
      <div className="ald-info-icon">
        {icon}
      </div>

      <div>
        <span>{label}</span>

        <strong className={capitalize ? "ald-capitalize" : ""}>
          {value === undefined ||
          value === null ||
          String(value).trim() === ""
            ? "Not provided"
            : String(value).trim()}
        </strong>
      </div>
    </div>
  );
}

/* ================================================================
   EDUCATION CARD
================================================================ */

function EducationCard({
  number,
  title,
  school,
  start,
  finish,
  certificate,
}) {
  return (
    <article className="ald-education-card">

      <div className="ald-education-top">
        <span>{number}</span>

        <div>
          <span>Education</span>
          <h3>{title}</h3>
        </div>

        <School />
      </div>

      <div className="ald-school-name">
        {school || "Institution not provided"}
      </div>

      <div className="ald-education-details">

        <div>
          <span>Started</span>
          <strong>{start || "—"}</strong>
        </div>

        <div>
          <span>Completed</span>
          <strong>{finish || "—"}</strong>
        </div>

        <div>
          <span>Certificate</span>
          <strong>{certificate || "—"}</strong>
        </div>

      </div>

    </article>
  );
}

/* ================================================================
   REFEREE CARD
================================================================ */

function RefereeCard({ number, title, referee }) {
  return (
    <article className="ald-referee-card">

      <div className="ald-referee-header">
        <div className="ald-referee-number">
          {number}
        </div>

        <div>
          <span>Reference</span>
          <h3>{title}</h3>
        </div>
      </div>

      <div className="ald-referee-content">

        <div>
          <span>Full name</span>
          <strong>{referee?.full_name || "Not provided"}</strong>
        </div>

        <div>
          <span>Phone</span>
          <strong>{referee?.phone || "Not provided"}</strong>
        </div>

        <div>
          <span>Email</span>
          <strong>{referee?.email || "Not provided"}</strong>
        </div>

        <div className="ald-referee-address">
          <span>Address</span>
          <strong>{referee?.address || "Not provided"}</strong>
        </div>

      </div>

    </article>
  );
}
